import React from "react";
import {Box} from "@mui/material";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import type {NodeViewProps} from "@tiptap/react";
import {variableChipSx} from "../../../common/variables/variableChipStyles";
import {getPrintoutVariableChipLabel} from "../../variables/printoutVariables";
import PrintoutBlockShell from "../shared/PrintoutBlockShell";
import FolioTableView from "./FolioTableView";
import {getFolioTableAttrs} from "./printoutFolioTableOptions";
import type {FolioResolved, PrintoutFolioTableAttrs} from "./printoutFolioTableOptions";

const VariableChip = ({variableKey}: {variableKey: string}) => (
    <Box className="printout-variable-chip" component="span" sx={variableChipSx}>
        {getPrintoutVariableChipLabel(variableKey)}
    </Box>
);

const EDITOR_TEMPLATE_ROW_COUNT = 3;

const PrintoutFolioTableNodeView = (props: NodeViewProps) => {
    const attrs = getFolioTableAttrs(props.node.attrs as PrintoutFolioTableAttrs);
    // The editor uses chip rows; preview/print receives resolved values.
    const resolved = (props.node.attrs as {resolved?: FolioResolved | null}).resolved ?? null;

    // Fall back to configured columns if resolution hides every column.
    const hiddenColumnIds = new Set(resolved?.hiddenColumnIds ?? []);
    const hiddenTotalIds = new Set(resolved?.hiddenTotalIds ?? []);
    const visibleColumns = attrs.columns.filter(column => !hiddenColumnIds.has(column.id));
    const columns = visibleColumns.length ? visibleColumns : attrs.columns;
    const totals = attrs.totals.filter(total => !hiddenTotalIds.has(total.id));

    // The editor represents day grouping with one variable-labelled sample group.
    const sections = resolved
        ? resolved.groups ?? null
        : (attrs.groupByDate
            ? [{
                label: <VariableChip variableKey="folioItemStartDate"/>,
                rowCount: EDITOR_TEMPLATE_ROW_COUNT,
                startIndex: 0,
            }]
            : null);

    return (
        <PrintoutBlockShell
            {...props}
            blockType="printoutFolioTable"
            icon={<ReceiptLongOutlinedIcon fontSize="small"/>}
            label="Folio table"
        >
            <Box className="printout-folio" sx={{width: "100%"}}>
                <FolioTableView
                    columns={columns}
                    inclusiveRowIndexes={resolved?.inclusiveRowIndexes ?? null}
                    renderCell={resolved
                        ? (column, rowIndex) => resolved.items[rowIndex]?.[column.id] ?? ""
                        : (column) => <VariableChip variableKey={column.variableKey}/>}
                    renderTotalValue={resolved
                        ? (total) => resolved.totals[total.id] ?? ""
                        : (total) => <VariableChip variableKey={total.variableKey}/>}
                    rowCount={resolved ? resolved.items.length : EDITOR_TEMPLATE_ROW_COUNT}
                    sections={sections}
                    totals={totals}
                />
            </Box>
        </PrintoutBlockShell>
    );
};

export default PrintoutFolioTableNodeView;
